import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { toast } from "sonner";
import {
  HandCoins, RefreshCw, CheckCircle2, XCircle, ArrowLeft,
  ChevronLeft, ChevronRight, ExternalLink, AlertTriangle, ScanSearch, Wrench,
} from "lucide-react";

const TYPE_LABELS = {
  Event: "Événement", Membership: "Adhésion", PaymentForm: "Paiement",
  Donation: "Don", Crowdfunding: "Crowdfunding", Shop: "Boutique", Checkout: "Encaissement",
};
const ETAT_LABELS = { Public: "Public", Private: "Privé", Draft: "Brouillon", Disabled: "Désactivé" };
const MAPPING_LABELS = {
  webinaire: "Webinaire / Événement", adhesion: "Adhésion", produit: "Produit", service: "Service",
};

const thCls = "text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-cfhtb-text";
const tdCls = "px-4 py-3 text-sm text-cfhtb-heading";
const inputCls = "w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-cfhtb-light";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR") : "—");
const fmtEur = (n) => `${(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €`;
const toInput = (d) => (d ? String(d).slice(0, 10) : "");

function Toggle({ on, onChange, testid }) {
  return (
    <button type="button" role="switch" aria-checked={on} data-testid={testid} onClick={onChange}
      className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${on ? "bg-cfhtb-primary" : "bg-slate-300"}`}>
      <span className="absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all"
        style={{ left: on ? "calc(100% - 22px)" : "2px" }} />
    </button>
  );
}

export default function HelloAssoAdmin() {
  const [statut, setStatut] = useState(null);
  const [forms, setForms] = useState(null);
  const [mappings, setMappings] = useState({});
  const [remarques, setRemarques] = useState([]);
  const [seminaires, setSeminaires] = useState([]);
  const [selection, setSelection] = useState(null);
  const [commandes, setCommandes] = useState(null);
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [syncBusy, setSyncBusy] = useState(null);
  const [migration, setMigration] = useState(null);
  const [migBusy, setMigBusy] = useState(false);
  const [reconForm, setReconForm] = useState(null);
  const [recon, setRecon] = useState(null);
  const [reconBusy, setReconBusy] = useState(false);
  const [fixBusy, setFixBusy] = useState(null);

  const chargerMappings = () =>
    api("/admin/helloasso/mappings").then((d) => setMappings(d.items)).catch(() => {});
  const chargerRemarques = () =>
    api("/admin/helloasso/remarques").then((d) => setRemarques(d.items)).catch(() => {});

  const apercuMigration = async () => {
    setMigBusy(true);
    try {
      const r = await api("/admin/helloasso/migration-votre-email", { method: "POST" });
      setMigration(r);
      toast.success(`Aperçu : ${r.a_modifier.length} à modifier, ${r.conflits.length} conflit(s)`);
    } catch (e) { toast.error(e.message); } finally { setMigBusy(false); }
  };
  const appliquerMigration = async () => {
    if (!window.confirm("Appliquer la bascule « votre email » sur les fiches concernées ?")) return;
    setMigBusy(true);
    try {
      const r = await api("/admin/helloasso/migration-votre-email?apply=true", { method: "POST" });
      setMigration(r);
      toast.success(`${r.modifies} fiche(s) mise(s) à jour`);
    } catch (e) { toast.error(e.message); } finally { setMigBusy(false); }
  };

  const charger = async () => {
    setBusy(true);
    try {
      const s = await api("/admin/helloasso/statut");
      setStatut(s);
      chargerMappings();
      chargerRemarques();
      api("/admin/seminaires")
        .then((r) => setSeminaires(Array.isArray(r) ? r : (r.items || r.seminaires || [])))
        .catch(() => {});
      if (s.connectee) {
        const f = await api("/admin/helloasso/formulaires");
        setForms(f.items);
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { charger(); }, []);

  const mappingOf = (slug) => mappings[slug] || {};

  const updateMapping = async (form, patch) => {
    const cur = mappingOf(form.slug);
    const body = {
      type_mapping: cur.type_mapping ?? null,
      sync_actif: cur.sync_actif ?? false,
      seminar_id: cur.seminar_id ?? null,
      adhesion_mode: cur.adhesion_mode ?? null,
      adhesion_debut: toInput(cur.adhesion_debut) || null,
      adhesion_fin: toInput(cur.adhesion_fin) || null,
      adhesion_duree_mois: cur.adhesion_duree_mois ?? null,
      ...patch,
    };
    try {
      await api(`/admin/helloasso/mappings/${form.type}/${form.slug}`, { method: "PUT", body });
      chargerMappings();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const runSync = async (form) => {
    setSyncBusy(form.slug);
    try {
      const r = await api(`/admin/helloasso/sync/${form.type}/${form.slug}`, { method: "POST" });
      const s = r.stats;
      toast.success(
        `Synchro terminée : ${s.commandes} commande(s) — ${s.importes} importée(s), ` +
        `${s.completes} complétée(s), ${s.inscriptions} inscription(s), ${s.adhesions} adhésion(s)` +
        (s.doublons ? ` — ${s.doublons} doublon(s) à traiter` : ""));
      chargerMappings();
      chargerRemarques();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSyncBusy(null);
    }
  };

  const remarqueTraitee = async (rem) => {
    try {
      await api(`/admin/helloasso/remarques/${rem.id}/traitee`, { method: "POST" });
      chargerRemarques();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const ouvrirCommandes = async (form, p = 1) => {
    setReconForm(null);
    setRecon(null);
    setSelection(form);
    setPage(p);
    setCommandes(null);
    try {
      const c = await api(`/admin/helloasso/formulaires/${form.type}/${form.slug}/commandes?page=${p}`);
      setCommandes(c);
    } catch (e) {
      toast.error(e.message);
      setCommandes({ items: [], total: 0, total_pages: 0 });
    }
  };

  const ouvrirRapprochement = async (form) => {
    setSelection(null);
    setReconForm(form);
    setRecon(null);
    setReconBusy(true);
    try {
      const r = await api(`/admin/helloasso/rapprochement/${form.type}/${form.slug}`);
      setRecon(r);
    } catch (e) {
      toast.error(e.message);
      setReconForm(null);
    } finally {
      setReconBusy(false);
    }
  };

  const corriger = async (orderId) => {
    setFixBusy(orderId);
    try {
      const r = await api("/admin/helloasso/rapprochement/corriger", {
        method: "POST",
        body: { form_type: reconForm.type, form_slug: reconForm.slug, order_id: orderId },
      });
      toast.success(`Corrigé : ${r.fiche.prenom} ${r.fiche.nom} <${r.fiche.email}>`);
      await ouvrirRapprochement(reconForm);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setFixBusy(null);
    }
  };

  return (
    <AppLayout variant="admin">
      <div data-testid="helloasso-page" className="space-y-6 animate-fade-in">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-serif text-3xl font-bold text-cfhtb-heading tracking-tight">HelloAsso</h1>
            <p className="text-sm text-cfhtb-text mt-1">
              Formulaires de votre organisation, mappings de synchronisation et commandes.
            </p>
          </div>
          <button data-testid="helloasso-refresh" onClick={charger} disabled={busy}
            className="inline-flex items-center gap-2 bg-white text-cfhtb-primary border border-cfhtb-primary rounded-md px-4 py-2 text-sm font-medium hover:bg-cfhtb-bg transition-colors disabled:opacity-50">
            <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} /> Actualiser
          </button>
        </div>

        <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-5 flex items-center gap-4" data-testid="helloasso-status">
          {statut?.connectee ? (
            <>
              <CheckCircle2 className="h-8 w-8 text-cfhtb-success shrink-0" />
              <div>
                <p className="font-medium text-cfhtb-heading">Connecté à HelloAsso</p>
                <p className="text-xs text-cfhtb-text">Organisation : <b>{statut.organisation}</b> — {forms ? `${forms.length} formulaire(s)` : "chargement…"} · la synchro automatique via webhook est active pour les formulaires dont le toggle est sur « oui ».</p>
              </div>
            </>
          ) : (
            <>
              <XCircle className="h-8 w-8 text-cfhtb-error shrink-0" />
              <div>
                <p className="font-medium text-cfhtb-heading">
                  {statut && !statut.configuree ? "Identifiants non configurés" : "Connexion impossible"}
                </p>
                <p className="text-xs text-cfhtb-text">{statut?.detail || "Vérifiez les clés HelloAsso côté serveur."}</p>
              </div>
            </>
          )}
        </div>

        {statut?.connectee && (
          <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-5 space-y-3" data-testid="helloasso-migration">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="font-serif text-lg font-semibold text-cfhtb-heading">Bascule « votre email »</h2>
                <p className="text-xs text-cfhtb-text mt-0.5">
                  Remplace l'email du payeur des fiches déjà importées par le champ « votre email » du participant. L'ancien email est conservé en secondaire. Lancez d'abord un aperçu.
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button data-testid="migration-apercu" onClick={apercuMigration} disabled={migBusy}
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-cfhtb-heading hover:bg-cfhtb-bg disabled:opacity-60">
                  {migBusy ? "…" : "Aperçu"}
                </button>
                <button data-testid="migration-appliquer" onClick={appliquerMigration}
                  disabled={migBusy || !migration || migration.a_modifier.length === 0}
                  className="inline-flex items-center gap-1.5 rounded-md bg-cfhtb-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-cfhtb-secondary disabled:opacity-60">
                  Appliquer{migration ? ` (${migration.a_modifier.length})` : ""}
                </button>
              </div>
            </div>
            {migration && (
              <div className="text-sm space-y-2" data-testid="migration-result">
                <p className="text-cfhtb-text">
                  Analysées : <b>{migration.analyses}</b> · À modifier : <b className="text-cfhtb-primary">{migration.a_modifier.length}</b> · Conflits : <b className="text-amber-600">{migration.conflits.length}</b>
                  {migration.modifies > 0 && <> · Modifiées : <b className="text-cfhtb-success">{migration.modifies}</b></>}
                </p>
                {migration.a_modifier.length > 0 && (
                  <ul className="divide-y divide-slate-100 rounded-md border border-slate-200 max-h-48 overflow-auto">
                    {migration.a_modifier.map((m) => (
                      <li key={m.person_id} className="px-3 py-2 text-xs flex flex-wrap gap-x-2">
                        <span className="font-medium text-cfhtb-heading">{m.label}</span>
                        <span className="text-cfhtb-text line-through">{m.actuel}</span>
                        <span className="text-cfhtb-primary">→ {m.nouveau}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {migration.conflits.length > 0 && (
                  <ul className="divide-y divide-amber-100 rounded-md border border-amber-200 bg-amber-50 max-h-40 overflow-auto" data-testid="migration-conflits">
                    {migration.conflits.map((c) => (
                      <li key={c.person_id} className="px-3 py-2 text-xs text-amber-900">
                        <b>{c.label}</b> : {c.actuel} → {c.cible} — déjà utilisé par {c.conflit_avec}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}

        {remarques.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 space-y-3" data-testid="helloasso-remarques">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <h2 className="font-serif text-lg font-semibold text-amber-800">
                À traiter ({remarques.length})
              </h2>
            </div>
            <ul className="space-y-2">
              {remarques.map((r) => (
                <li key={r.id} className="flex flex-wrap items-center justify-between gap-3 bg-white/70 rounded-md px-4 py-2.5"
                  data-testid={`remarque-${r.id}`}>
                  <p className="text-sm text-amber-900">{r.message}</p>
                  <button data-testid={`remarque-traitee-${r.id}`} onClick={() => remarqueTraitee(r)}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-cfhtb-primary hover:text-cfhtb-secondary transition-colors shrink-0">
                    <CheckCircle2 className="h-4 w-4" /> Marquer traitée
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {reconForm ? (
          <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm overflow-hidden" data-testid="helloasso-recon-panel">
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <button data-testid="recon-back" onClick={() => { setReconForm(null); setRecon(null); }}
                  className="p-1.5 rounded text-cfhtb-text hover:bg-slate-100 transition-colors">
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <div>
                  <h2 className="font-serif text-lg font-semibold text-cfhtb-heading">Rapprochement — {reconForm.titre || reconForm.slug}</h2>
                  <p className="text-xs text-cfhtb-text">Inscriptions HelloAsso (source) comparées aux fiches de l'extranet.</p>
                </div>
              </div>
              <button data-testid="recon-refresh" onClick={() => ouvrirRapprochement(reconForm)} disabled={reconBusy}
                className="inline-flex items-center gap-2 bg-white text-cfhtb-primary border border-cfhtb-primary rounded-md px-3 py-1.5 text-sm font-medium hover:bg-cfhtb-bg disabled:opacity-50">
                <RefreshCw className={`h-4 w-4 ${reconBusy ? "animate-spin" : ""}`} /> Relancer
              </button>
            </div>
            {!recon ? (
              <p className="px-5 py-10 text-center text-sm text-cfhtb-text" data-testid="recon-loading">{reconBusy ? "Analyse en cours…" : "—"}</p>
            ) : (
              <div className="p-5 space-y-5">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="recon-resume">
                  {[
                    ["Inscriptions", recon.resume.inscriptions, "text-cfhtb-heading"],
                    ["Participants uniques", recon.resume.participants_uniques, "text-cfhtb-heading"],
                    ["Dans l'extranet", recon.resume.dans_extranet, "text-cfhtb-success"],
                    ["Manquants", recon.resume.manquants, "text-cfhtb-error"],
                    ["Mal rattachés", recon.resume.mal_rattaches, "text-amber-600"],
                    ["Doublons", recon.resume.doublons, "text-amber-600"],
                  ].map(([label, val, cls]) => (
                    <div key={label} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
                      <p className="text-[11px] uppercase tracking-wide text-cfhtb-text">{label}</p>
                      <p className={`text-xl font-bold ${cls}`}>{val}</p>
                    </div>
                  ))}
                </div>
                {recon.anomalies.length === 0 ? (
                  <div className="flex items-center gap-2 rounded-md bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-800" data-testid="recon-ok">
                    <CheckCircle2 className="h-5 w-5" /> Aucune anomalie : toutes les inscriptions sont rattachées à la bonne fiche.
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <table className="w-full" data-testid="recon-table">
                      <thead className="bg-slate-50 border-b border-slate-100">
                        <tr>
                          <th className={thCls}>Participant</th><th className={thCls}>Email participant</th>
                          <th className={thCls}>Payeur</th><th className={thCls}>Anomalie</th><th className={thCls}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {recon.anomalies.map((a) => (
                          <tr key={a.order_id} className="border-b border-slate-50 align-top hover:bg-cfhtb-bg/40" data-testid={`recon-row-${a.order_id}`}>
                            <td className={tdCls}>{a.participant || "—"}{a.doublon && <span className="ml-1 text-[10px] text-amber-600">(doublon)</span>}</td>
                            <td className={tdCls}>{a.votre_email || <span className="text-cfhtb-text/60">{a.payeur_email}</span>}</td>
                            <td className={tdCls}>{a.payeur || "—"}<br /><span className="text-xs text-cfhtb-text/60">{a.payeur_email}</span></td>
                            <td className={tdCls}>
                              <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${a.statut === "manquant" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                                {a.statut === "manquant" ? "Manquant" : "Mal rattaché"}
                              </span>
                              {a.detail && <p className="text-xs text-cfhtb-text mt-1">{a.detail}</p>}
                            </td>
                            <td className={`${tdCls} text-right`}>
                              <button data-testid={`recon-fix-${a.order_id}`} onClick={() => corriger(a.order_id)} disabled={fixBusy === a.order_id}
                                className="inline-flex items-center gap-1.5 rounded-md bg-cfhtb-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-cfhtb-secondary disabled:opacity-50">
                                <Wrench className={`h-3.5 w-3.5 ${fixBusy === a.order_id ? "animate-pulse" : ""}`} /> Corriger
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : selection ? (
          <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm overflow-hidden" data-testid="helloasso-orders-panel">
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <button data-testid="helloasso-back" onClick={() => setSelection(null)}
                  className="p-1.5 rounded text-cfhtb-text hover:bg-slate-100 transition-colors">
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <div>
                  <h2 className="font-serif text-lg font-semibold text-cfhtb-heading">{selection.titre || selection.slug}</h2>
                  <p className="text-xs text-cfhtb-text">
                    {TYPE_LABELS[selection.type] || selection.type}
                    {commandes && ` — ${commandes.total ?? "?"} commande(s)`}
                    {commandes ? ` — page totalisant ${fmtEur(commandes.total_page_euros)}` : ""}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid="helloasso-prev-page" disabled={page <= 1} onClick={() => ouvrirCommandes(selection, page - 1)}
                  className="p-1.5 rounded border border-slate-300 text-cfhtb-text hover:bg-cfhtb-bg disabled:opacity-40 transition-colors">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-sm text-cfhtb-text" data-testid="helloasso-page-indicator">Page {page}{commandes?.total_pages > 0 ? ` / ${commandes.total_pages}` : ""}</span>
                <button data-testid="helloasso-next-page" disabled={commandes?.total_pages > 0 && page >= commandes.total_pages}
                  onClick={() => ouvrirCommandes(selection, page + 1)}
                  className="p-1.5 rounded border border-slate-300 text-cfhtb-text hover:bg-cfhtb-bg disabled:opacity-40 transition-colors">
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="helloasso-orders-table">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className={thCls}>Date</th><th className={thCls}>Payeur</th>
                    <th className={thCls}>Email</th><th className={thCls}>Articles</th>
                    <th className={thCls}>Montant</th><th className={thCls}>État</th>
                  </tr>
                </thead>
                <tbody>
                  {!commandes ? (
                    <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-cfhtb-text">Chargement…</td></tr>
                  ) : commandes.items.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-cfhtb-text" data-testid="helloasso-orders-empty">Aucune commande sur ce formulaire.</td></tr>
                  ) : commandes.items.map((o) => (
                    <tr key={o.id} className="border-b border-slate-50 hover:bg-cfhtb-bg/40" data-testid={`helloasso-order-${o.id}`}>
                      <td className={tdCls}>{fmtDate(o.date)}</td>
                      <td className={tdCls}>{o.payeur || "—"}</td>
                      <td className={tdCls}>{o.email || "—"}</td>
                      <td className={tdCls}>{o.nb_articles}</td>
                      <td className={tdCls}>{fmtEur(o.montant)}</td>
                      <td className={tdCls}>{o.etat || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : forms && (
          <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm overflow-x-auto">
            <table className="w-full" data-testid="helloasso-forms-table">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className={thCls}>Formulaire</th><th className={thCls}>Type</th>
                  <th className={thCls}>Mapping</th><th className={thCls}>Configuration</th>
                  <th className={thCls}>Synchro</th><th className={thCls}></th>
                </tr>
              </thead>
              <tbody>
                {forms.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-cfhtb-text" data-testid="helloasso-forms-empty">Aucun formulaire trouvé pour cette organisation.</td></tr>
                ) : forms.map((f) => {
                  const m = mappingOf(f.slug);
                  return (
                    <tr key={`${f.type}-${f.slug}`} className="border-b border-slate-50 align-top hover:bg-cfhtb-bg/40" data-testid={`helloasso-form-${f.slug}`}>
                      <td className={tdCls}>
                        <span className="font-medium">{f.titre || f.slug}</span>
                        {f.url && (
                          <a href={f.url} target="_blank" rel="noreferrer"
                            className="ml-2 inline-flex items-center text-cfhtb-secondary hover:text-cfhtb-primary align-middle">
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        )}
                        <p className="text-xs text-cfhtb-text/70 mt-0.5">
                          {TYPE_LABELS[f.type] || f.type} · {ETAT_LABELS[f.etat] || f.etat || "—"}
                          {m.type_mapping && (
                            <span data-testid={`ha-last-sync-${f.slug}`}><br />
                              {m.derniere_sync_le
                                ? <>Dernière synchro {new Date(m.derniere_sync_le).toLocaleString("fr-FR")}
                                    {m.stats ? ` : ${m.stats.importes} importée(s), ${m.stats.completes} complétée(s)` : ""}</>
                                : "Jamais synchronisé — auto via webhook + synchro quotidienne (6h)"}
                            </span>
                          )}
                        </p>
                      </td>
                      <td className={tdCls}>
                        <select data-testid={`ha-type-${f.slug}`} className={inputCls}
                          value={m.type_mapping || ""}
                          onChange={(e) => updateMapping(f, { type_mapping: e.target.value || null, sync_actif: false })}>
                          <option value="">— Non mappé —</option>
                          {Object.entries(MAPPING_LABELS).map(([v, l]) => (
                            <option key={v} value={v}>{l}</option>
                          ))}
                        </select>
                      </td>
                      <td className={tdCls}>
                        {m.type_mapping === "webinaire" && (
                          <select data-testid={`ha-seminar-${f.slug}`} className={inputCls}
                            value={m.seminar_id || ""}
                            onChange={(e) => updateMapping(f, { seminar_id: e.target.value || null })}>
                            <option value="">— Choisir un événement —</option>
                            <option value="new">＋ Créer un événement depuis ce formulaire</option>
                            {seminaires.map((s) => (
                              <option key={s.id || s._id} value={s.id || s._id}>{s.titre}</option>
                            ))}
                          </select>
                        )}
                        {m.type_mapping === "adhesion" && (
                          <div className="space-y-2" data-testid={`ha-adhesion-config-${f.slug}`}>
                            <select className={inputCls} data-testid={`ha-adhesion-mode-${f.slug}`}
                              value={m.adhesion_mode || ""}
                              onChange={(e) => updateMapping(f, { adhesion_mode: e.target.value || null })}>
                              <option value="">— Mode de période —</option>
                              <option value="fixe">Période fixe (dates)</option>
                              <option value="glissante">Durée glissante (à partir du paiement)</option>
                            </select>
                            {m.adhesion_mode === "fixe" && (
                              <div className="flex gap-2">
                                <input type="date" className={inputCls} data-testid={`ha-adhesion-debut-${f.slug}`}
                                  value={toInput(m.adhesion_debut)}
                                  onChange={(e) => updateMapping(f, { adhesion_debut: e.target.value || null })} />
                                <input type="date" className={inputCls} data-testid={`ha-adhesion-fin-${f.slug}`}
                                  value={toInput(m.adhesion_fin)}
                                  onChange={(e) => updateMapping(f, { adhesion_fin: e.target.value || null })} />
                              </div>
                            )}
                            {m.adhesion_mode === "glissante" && (
                              <div className="flex items-center gap-2">
                                <input type="number" min="1" max="60" className={`${inputCls} w-20`}
                                  data-testid={`ha-adhesion-duree-${f.slug}`}
                                  value={m.adhesion_duree_mois || ""}
                                  onChange={(e) => updateMapping(f, { adhesion_duree_mois: e.target.value ? parseInt(e.target.value, 10) : null })} />
                                <span className="text-xs text-cfhtb-text">mois</span>
                              </div>
                            )}
                          </div>
                        )}
                        {!["webinaire", "adhesion"].includes(m.type_mapping) && (
                          <span className="text-xs text-cfhtb-text/60">—</span>
                        )}
                      </td>
                      <td className={tdCls}>
                        <div className="flex items-center gap-2">
                          <Toggle testid={`ha-sync-toggle-${f.slug}`} on={!!m.sync_actif}
                            onChange={() => updateMapping(f, { sync_actif: !m.sync_actif })} />
                          <span className="text-xs text-cfhtb-text">{m.sync_actif ? "Oui" : "Non"}</span>
                        </div>
                      </td>
                      <td className={`${tdCls} text-right`}>
                        <div className="flex flex-col items-end gap-1.5">
                          <button data-testid={`helloasso-orders-${f.slug}`} onClick={() => ouvrirCommandes(f)}
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-cfhtb-primary hover:text-cfhtb-secondary transition-colors">
                            <HandCoins className="h-4 w-4" /> Commandes
                          </button>
                          {m.type_mapping === "webinaire" && m.seminar_id && (
                            <button data-testid={`helloasso-recon-${f.slug}`} onClick={() => ouvrirRapprochement(f)}
                              className="inline-flex items-center gap-1.5 text-sm font-medium text-cfhtb-secondary hover:text-cfhtb-primary transition-colors">
                              <ScanSearch className="h-4 w-4" /> Rapprochement
                            </button>
                          )}
                          <button data-testid={`ha-sync-run-${f.slug}`} onClick={() => runSync(f)}
                            disabled={!m.sync_actif || syncBusy === f.slug}
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-cfhtb-secondary hover:text-cfhtb-primary transition-colors disabled:opacity-40">
                            <RefreshCw className={`h-4 w-4 ${syncBusy === f.slug ? "animate-spin" : ""}`} />
                            {syncBusy === f.slug ? "Synchro…" : "Synchroniser"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
